// Shared visible/sorted-keys resolver for Traffic data tabs.
// Used by `Traffic.tsx` (column construction), `TrafficEditableThead`
// (drag/drop + sort-fallback), and `TrafficColumnsPickerButton`
// (which columns are already shown). Centralizing it here ensures all
// three stay in lockstep — previously this 12-line block was triplicated.

interface ColumnConfig {
  invisible?: boolean
  order?: number
  sort?: string | null
}

/**
 * Resolve the visible+sorted column keys for a Traffic tab. A column is
 * visible when the user's per-tab config says so, falling back to the
 * report's default-invisible set. Within the visible set, sort by the
 * user's saved `order`; columns without an order come after, in the
 * canonical `allKeys` sequence.
 */
export function resolveVisibleSortedKeys(
  allKeys: readonly string[],
  columnConfigs: Record<string, ColumnConfig>,
  defaultInvisible: Set<string>,
): string[] {
  return allKeys
    .filter((k) => {
      const userInvisible = columnConfigs[k]?.invisible
      if (userInvisible !== undefined) return !userInvisible
      return !defaultInvisible.has(k)
    })
    .sort((a, b) => {
      const oa = columnConfigs[a]?.order
      const ob = columnConfigs[b]?.order
      if (oa != null && ob != null) return oa - ob
      if (oa != null) return -1
      if (ob != null) return 1
      return allKeys.indexOf(a) - allKeys.indexOf(b)
    })
}

/**
 * Parse the comma-separated default-invisible-columns string for a report
 * type into a Set. Returns an empty Set when the report has no defaults.
 */
export function parseDefaultInvisibleSet(defaultInvisibleStr: string | undefined): Set<string> {
  if (!defaultInvisibleStr) return new Set()
  return new Set(defaultInvisibleStr.split(',').filter(Boolean))
}
